import constants from '../../constants';
import PubSubUtils from '../../utils/pubsub-utils';

import * as actionType from './actions/index';
import { getStore, dispatch, getValue } from './state-manager';
import { mergeData } from './actions/action-creators';

/**
 * Provides APIs to set, get, remove and merge in suggestion Context
 * This class is independent of the context
 */
class SuggestionsContext {
  private context: string;
  private static _instance: SuggestionsContext;
  constructor(context: string) {
    SuggestionsContext._instance = this;
    this.context = `${context}/${constants.SUGGESTION}`;
    if (!getStore()?.getState().data?.[this.context]) {
      dispatch({
        type: actionType.ADD_SUGGESTION_CONTEXT,
        payload: {
          context: this.context,
          data: {}
        }
      });
    }
  }

  /**
   * Obtains the value of a specified field from the Suggestions Context.
   * @param property - The field whose value is obtained from the Suggestions Context.
   * @returns - The value of the field
   * @example <caption>Example for getField</caption>
   * In this example, the API obtains the value of the caseInfo.content.EmailField field from the Suggestions Context.
   * PCore.getStateUtils().getSuggestionsContext('app/primary_1/workarea_1').getField('caseInfo.content.EmailField')
   */
  getField(property: string): string {
    return getValue(property, '', this.context);
  }

  /**
   * Assigns a value to a specified field in the Suggestions Context.
   * @param property - The field that is assigned a value in the Suggestions Context.
   * @param value - The value that is assigned to a field in the Suggestions Contex
   * @example <caption>Example for setField</caption>
   * In this example, the suggestion@gmail.com value is assigned to the caseInfo.content.EmailField field.
   * PCore.getStateUtils().getSuggestionsContext('app/primary_1/workarea_1').setField('caseInfo.content.EmailField','suggestion@gmail.com')
   */
  setField(property: string, value: string) {
    dispatch({
      type: actionType.SET_PROPERTY,
      payload: {
        reference: property,
        value,
        context: this.context
      }
    });
  }

  /**
   * Deletes the value of a specified field from the Suggestions Context.
   * @param property - The field whose value is deleted from the Suggestions Context.
   * @example <caption>Example for removeField</caption>
   * In this example, the value of the caseInfo.content.EmailField field is deleted from the Suggestions Context.
   * PCore.getStateUtils().getSuggestionsContext('app/primary_1/workarea_1').removeField('caseInfo.content.EmailField')
   */
  removeField(property: string | number, options: { actionPerformed?: string } = {}) {
    dispatch({
      type: actionType.REMOVE_ITEM,
      payload: {
        reference: property,
        context: this.context,
        ...options
      }
    });
    PubSubUtils.publish(constants.PUB_SUB_EVENTS.SUGGESTION_PROCESSED, {
      reference: property,
      context: this.context,
      ...options
    });
  }

  /**
   * Updates a state in the Suggestions Context.
   * @param stateObj - The object containing the fields and values that must be updated in the Suggestions Context.
   * @example <caption>Example for setState</caption>
   * In this example, the API updates the values of the fields in the caseInfo object in the Suggestions Context.
   * PCore.getStateUtils().getSuggestionsContext('app/primary_1/workarea_1').setState({
   *   caseInfo: {
   *    content: {
   *      EmailField: 'suggestion@gmail.com',
   *       TextField: 'suggestion'
   *      }
   *    }
   *  })
   */
  setState(stateObj: object) {
    dispatch(
      mergeData({
        context: this.context,
        data: stateObj,
        isArrayDeepMerge: true
      })
    );
  }
}

export default SuggestionsContext;
